import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { setToken } from "../lib/nativeTokenStorage";
import { isNative } from "../lib/nativePlatform";
import { readWebAttribution } from "../lib/webAttribution";
import { startGoogleLogin } from "../lib/googleAuth";
import { toast } from "sonner";

// Iter 54 — safe return_to whitelist. Any value not in this set
// (or any /chat / /plus / /upgrade / /reflect path) is dropped on the
// floor — prevents open-redirect abuse AND prevents users landing on
// legacy pre-WhatsApp pages (/dashboard, /onboarding) after login.
const _SAFE_RETURN_PREFIXES = [
  "/chat", "/plus", "/upgrade", "/reflect", "/r/",
  "/report-card", "/me", "/share/", "/report/", "/pass/",
];
function sanitizeReturnTo(raw) {
  if (!raw || typeof raw !== "string") return "/chat";
  // Must be a relative path on our own origin — no protocol, no host.
  if (raw.startsWith("//") || /^https?:/i.test(raw)) return "/chat";
  if (!raw.startsWith("/")) return "/chat";
  // Allow only known-good prefixes; everything else (including the
  // legacy /dashboard and /onboarding) goes to /chat.
  if (_SAFE_RETURN_PREFIXES.some((p) => raw === p || raw.startsWith(p + "?") || raw.startsWith(p + "/"))) {
    return raw;
  }
  return "/chat";
}

export default function Login() {
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser, user, logout } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Iter 54 — return_to: where to send the user after a successful sign-in.
  // Falls through to /chat (NOT legacy /dashboard) when unset/invalid.
  const returnTo = sanitizeReturnTo(params.get("return_to"));

  // Fire `gully_plus_login_required` analytics if user arrived from
  // the Plus page (helps us measure how many people bounce off login).
  useEffect(() => {
    if (returnTo.startsWith("/plus") || returnTo.startsWith("/upgrade")) {
      try {
        api.post("/client-error", {
          kind: "gully_plus_login_required",
          session_id: null,
          payload: { return_to: returnTo, mode },
        }).catch(() => {});
      } catch { /* swallow */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      // XX.292 P0 — Before a Create Account submission, hard sign-out
      // any prior session on this browser. This guarantees that a 400
      // "email already registered" response can NEVER land in a
      // browser still carrying stale token / cookie / session_id
      // state from a previous authenticated user. Server-side
      // /auth/register is already provably non-authenticating on
      // duplicate email (see backend
      // test_iter_xx292_auth_register_dup_contract). This is the
      // matching client-side hardening so a bystander session cannot
      // be mistaken for the outcome of the failed submission.
      if (mode === "register" && user) {
        try { await logout(); } catch { /* fail-open */ }
      }
      // Iter XX.285 — Attach the latest known web attribution to the
      // register payload ONLY. Login is unaffected. Attribution is
      // completely non-blocking: read errors → null → server ignores.
      // Iter XX.285b — Switched from dynamic `import("@/lib/…")` (which
      // was a lazy chunk) to a static top-of-file import so a chunk-
      // load hiccup can never silently drop the attribution.
      let _webAttribution = null;
      if (mode !== "login") {
        try { _webAttribution = readWebAttribution(); }
        catch { /* fail-soft */ }
      }
      const body = mode === "login"
        ? { email, password }
        : { email, password, name,
            ...(_webAttribution ? { web_attribution: _webAttribution } : {}) };
      const { data } = await api.post(path, body);
      if (data.token) await setToken(data.token);
      setUser(data.user);
      // Iter XX.197 — attach the (previously-anonymous) RevenueCat
      // customer to the real Gully user_id so a Play purchase made
      // pre-login is credited to the user post-login. No-op on web
      // and on native builds without RC configured.
      if (data.user?.user_id) {
        let _rcAppUserId = null;
        try {
          const { logInRevenueCat } = await import("@/lib/revenueCat");
          const _rcRes = await logInRevenueCat(data.user.user_id);
          _rcAppUserId = _rcRes?.customerInfo?.originalAppUserId
            || _rcRes?.customerInfo?.original_app_user_id
            || null;
        } catch { /* swallow — non-fatal, native-only */ }
        // Iter XX.241 — Canonical Plus reconciliation on login. This
        // is what resolves the founder's iOS "purchased on Apple but
        // Gully still says Upgrade" case: any RC-linked active
        // entitlement is pulled from the RC REST API and stamped
        // onto `user_billing`, so the very next `/auth/me` reads it
        // back as `gully_plus.active = true`.
        try {
          const { syncGullyPlus } = await import("@/lib/entitlements");
          await syncGullyPlus({ rcAppUserId: _rcAppUserId || undefined });
        } catch { /* swallow — never block login on entitlement sync */ }
      }
      // Iter XX.43 — apply the stored referral cookie on new-account
      // creation so first-touch partner attribution is written server-side.
      if (mode === "register" && data.user?.user_id) {
        try {
          const { getStoredReferral } = await import("@/lib/referral");
          const ref = getStoredReferral();
          if (ref) {
            await api.post("/partners/apply-referral", { ref }).catch(() => {});
          }
        } catch { /* swallow */ }
      }
      // Iter 54 — Honour return_to. The previous logic forcibly sent
      // every login to /onboarding or /dashboard (legacy pre-WhatsApp
      // pages), trapping users in the old onboarding even when they
      // started from /chat or clicked Upgrade.
      try {
        api.post("/client-error", {
          kind: "gully_plus_login_returned",
          session_id: null,
          payload: { return_to: returnTo, mode },
        }).catch(() => {});
      } catch { /* swallow */ }
      navigate(returnTo, { replace: true });
    } catch (err) {
      // 2026-06-25 — Iter 25.9j. Specific reason + next step.
      // 2026-10-04 — Google-created accounts have no Gully password;
      // guide them to Continue with Google instead of "Invalid credentials".
      const detail = err.response?.data?.detail;
      toast.error(
        detail === "google_account_no_password"
          ? "This account was created with Google, so it doesn't have a Gully password. Please use Continue with Google to sign in."
          : detail
          || "Couldn't sign you in just now — check your connection and try again. If it sticks, give it a minute."
      );
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Iter 54 — preserve return_to across the Google round-trip by
    // appending it to the redirect URL. Sanitized above so an attacker
    // can't smuggle an off-site URL.
    // Card 4 — extracted into `lib/googleAuth.js` so the sidebar and
    // IdentitySheet reuse the identical flow. Behavior unchanged.
    startGoogleLogin(returnTo);
  };

  return (
    <div className="grain min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: "var(--bg-base)" }}>
      <div className="w-full max-w-md">
        <Link to="/" className="block mb-12">
          <span className="font-display text-3xl tracking-tight">gully</span>
        </Link>
        <p className="label-eyebrow mb-3">{mode === "login" ? "welcome back" : "create your account"}</p>
        <h1 className="font-display font-light text-3xl sm:text-4xl tracking-tight mb-10">
          {mode === "login" ? "Sign in to continue." : "Begin your Cricket Review journey."}
        </h1>

        <button
          data-testid="google-signin-btn"
          onClick={googleLogin}
          className="btn-ghost w-full mb-6 flex items-center justify-center gap-3"
          hidden={isNative()}
          style={isNative() ? { display: "none" } : undefined}
        >
          <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" opacity=".7"/><path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" opacity=".5"/><path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" opacity=".85"/></svg>
          Continue with Google
        </button>

        {/* Iter XX.181 — Native shells hide the Google Sign-In button.
            The Emergent OAuth flow expects an HTTPS-origin redirect; on
            Capacitor iOS the redirect target is `capacitor://localhost`
            which Safari cannot open ("Safari cannot open the page
            because the address is invalid"). Email / password + magic
            link remain fully functional on native and the JWT is
            stored in Keychain (Iter XX.177). Native Google Sign-In +
            Sign in with Apple ship together in v1.1 (App Store 4.8
            requires them paired). Web behavior is unchanged. */}

        <div
          className="flex items-center gap-4 mb-6"
          hidden={isNative()}
          style={isNative() ? { display: "none" } : undefined}
        >
          <div className="divider-x flex-1" />
          <span className="text-xs text-neutral-600 uppercase tracking-widest">or</span>
          <div className="divider-x flex-1" />
        </div>

        <form onSubmit={submit} className="space-y-4" data-clarity-mask="true">
          {/* Iter XX.79 — Whole auth form masked in Clarity recordings.
              Login is a "sensitive route" so recording is already
              stopped by ClarityProvider — this attribute is belt +
              braces so heatmaps of clicks around inputs never
              accidentally leak the typed characters. */}
          {mode === "register" && (
            <input
              data-testid="signup-name-input"
              type="text"
              placeholder="Your name"
              className="input-dark"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          )}
          <input
            data-testid="auth-email-input"
            type="email"
            placeholder="Email"
            className="input-dark"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            data-testid="auth-password-input"
            type="password"
            placeholder="Password"
            className="input-dark"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
          />
          <button
            data-testid="auth-submit-btn"
            type="submit"
            disabled={busy}
            className="btn-primary w-full"
          >
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        <button
          data-testid="auth-toggle-mode"
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          className="mt-6 text-sm text-neutral-500 hover:text-neutral-300 transition-colors"
        >
          {mode === "login" ? "New to Gully? Create an account →" : "Already have an account? Sign in →"}
        </button>
      </div>
    </div>
  );
}
